/**
 * R5a — the metric that decides whether the console was a mistake.
 *
 * PRD v4.0 promotes the console to the primary surface and demotes the Morning
 * Nob to the primary notification (ruling C7). The risk it takes on is stated in
 * v4.0's own risk table: **a conversation list is structurally an inbox**, and
 * the Morning Nob was built specifically so this product would not ship one. R5a
 * is the guard, and our tiebreak sentence is what makes it a guard rather than a
 * dashboard: *if the console lands and this number falls, it is the console that
 * gives way.* A number nobody can compute cannot make anything give way, so it
 * is computed here.
 *
 * WHAT R5a NEEDS, AND WHY IT IS TWO EVENTS RATHER THAN ONE.
 *
 * The threshold reads "satisfied owners with ≤ 3 sessions/week ≥ 40%", and its
 * older phrasing — the one the tiebreak was written against — reads "satisfied
 * recipients who never open the inbox". Both halves are behavioural except
 * satisfaction, which is a survey answer and joins in from outside; what the
 * browser has to supply is (1) how many console sessions an owner had, and (2)
 * whether any of them opened the inbox. So:
 *
 *   `console_session_started` — the DENOMINATOR. One per console session per
 *     door owner. Counted per owner per week it is the session count R5a
 *     thresholds at three.
 *   `console_inbox_opened`    — the DISQUALIFIER. One per console session, the
 *     first time an incoming knock is opened in it.
 *
 * The share R5a wants is then `1 − owners(inbox_opened) / owners(session_started)`
 * over the week, and the session-count half falls out of the same denominator.
 *
 * WHY NOT A SINGLE `console_session_without_inbox_open`. Because that event can
 * only be known when a session ENDS, and a browser session ends without telling
 * anybody: `pagehide` is dropped on a crash, on a killed tab, and unreliably on
 * mobile Safari. Every one of those losses removes a session that had NO inbox
 * open — the exact population in the numerator — so the metric would drift
 * upward toward 40% precisely as it got less trustworthy. A gate that fails safe
 * in the wrong direction is worse than no gate. Two events emitted at moments
 * that certainly happen have no such bias.
 *
 * WHAT COUNTS AS "OPENING THE INBOX". There is no inbox room any more; the list
 * is the surface. So the drill-down that R5a is about is opening an INCOMING
 * knock — reading somebody's hearing rather than living off the briefing. The
 * owner's own Nob thread is not an inbox open (it is where the Morning Nob is
 * read), and neither is a knock the owner themselves sent.
 *
 * SESSION GRAIN is `sessionStorage`: one tab visit, which is what "a session"
 * means to the person having one. Every access is wrapped — a private window, a
 * browser refusing site data, or a test environment without it must degrade to
 * "emit once per mount", never to a thrown error inside the shell.
 */
import { useEffect, useRef } from "react";
import { track } from "../../lib/telemetry";
import type { Conversation } from "../../lib/conversations";

const SESSION_KEY = "nob.r5a.session";
const INBOX_KEY = "nob.r5a.inbox";

/**
 * Claim a once-per-session flag. `true` means this caller got it.
 *
 * When storage is unavailable the answer is `true` — the event is emitted, and
 * the worst case is a duplicate within one mount rather than a silent hole in
 * the denominator. Over-counting a session is visible in the data; missing one
 * is not.
 */
function claimOnce(key: string): boolean {
  try {
    if (window.sessionStorage.getItem(key)) return false;
    window.sessionStorage.setItem(key, "1");
    return true;
  } catch {
    return true;
  }
}

/**
 * Instrument one console session. Owners only: R5a is a statement about people
 * who have a door, and a knocker with no door has no inbox to not open.
 */
export function useR5a(input: { hasDoor: boolean; active: Conversation | undefined }): void {
  const { hasDoor, active } = input;

  // The denominator, once the session is known to belong to a door owner.
  const counted = useRef(false);
  useEffect(() => {
    if (!hasDoor || counted.current) return;
    counted.current = true;
    if (claimOnce(SESSION_KEY)) track({ name: "console_session_started" });
  }, [hasDoor]);

  // The disqualifier. Reads the OPEN row rather than the click, so it cannot be
  // fooled by a row that was selected and never resolved to a knock.
  const opened = useRef(false);
  useEffect(() => {
    if (!hasDoor || opened.current) return;
    if (!active || active.kind !== "knock" || active.direction !== "incoming") return;
    // A held spot is not a knock and opening one is not opening the inbox:
    // there is no hearing behind it to read (FR-6.3).
    if (active.stage === "pre-knock") return;
    opened.current = true;
    if (claimOnce(INBOX_KEY)) track({ name: "console_inbox_opened" });
  }, [active, hasDoor]);
}
